/**
 * Kontrak "Gaya & aturan Jenna" (keputusan #59): gaya bicara per kanal,
 * kata ekspresif, dan larangan tambahan admin. Dipakai prompt server,
 * validasi server, dan form `/admin/ai-assistant`.
 */

export const STYLE_PRESETS = ["formal", "ramah", "santai"] as const;
export type StylePreset = (typeof STYLE_PRESETS)[number];

export const EXPRESSIVE_FREQUENCIES = ["jarang", "sedang"] as const;
export type ExpressiveFrequency = (typeof EXPRESSIVE_FREQUENCIES)[number];

export const WAIT_SOUND_VOLUMES = ["pelan", "sedang", "keras"] as const;
export type WaitSoundVolume = (typeof WAIT_SOUND_VOLUMES)[number];

export interface ExtraRuleDto {
	larangan: string;
	pengganti: string;
	aktif: boolean;
}

export interface AssistantStyleDto {
	chatStyle: StylePreset;
	voiceStyle: StylePreset;
	expressiveWords: string[];
	expressiveFrequency: ExpressiveFrequency;
	extraRules: ExtraRuleDto[];
	/** Bunyi napas saat menunggu jawaban suara (#60). */
	waitSoundEnabled: boolean;
	waitSoundVolume: WaitSoundVolume;
	/** Tunjuk otomatis saat pengguna meminta menampilkan/menunjukkan sesuatu (#63). */
	autoPointEnabled: boolean;
	/** Sakelar eksperimen suara (#62) — bawaan mati = perilaku MVP. */
	voiceFillerEnabled: boolean;
	voiceEchoFilterEnabled: boolean;
	voiceOpenerEnabled: boolean;
	voiceStyleLayerEnabled: boolean;
	/** Kalimat tunggu netral yang boleh diucapkan GPT-Live saat menunggu jawaban (dasar, bukan eksperimen). */
	voiceWaitPhrases: string[];
}

/** Sakelar eksperimen suara (#62) yang dibawa ke klien lewat respons mulai sesi. */
export interface VoiceExperimentFlags {
	filler: boolean;
	echoFilter: boolean;
	waitSound: boolean;
	opener: boolean;
	styleLayer: boolean;
}

/** Semua mati = perilaku MVP. */
export const VOICE_FLAGS_OFF: Readonly<VoiceExperimentFlags> = {
	filler: false,
	echoFilter: false,
	waitSound: false,
	opener: false,
	styleLayer: false,
};

export function voiceFlagsOf(
	style: Pick<
		AssistantStyleDto,
		| "voiceFillerEnabled"
		| "voiceEchoFilterEnabled"
		| "waitSoundEnabled"
		| "voiceOpenerEnabled"
		| "voiceStyleLayerEnabled"
	>,
): VoiceExperimentFlags {
	return {
		filler: style.voiceFillerEnabled,
		echoFilter: style.voiceEchoFilterEnabled,
		waitSound: style.waitSoundEnabled,
		opener: style.voiceOpenerEnabled,
		styleLayer: style.voiceStyleLayerEnabled,
	};
}

/** Body `PUT /api/admin/ai-assistant/style`. */
export type AssistantStyleUpdate = AssistantStyleDto;

export const STYLE_LIMITS = {
	extraRulesMax: 10,
	extraRuleCharsMax: 200,
	expressiveWordsMax: 10,
	expressiveWordCharsMax: 30,
	waitPhrasesMin: 1,
	waitPhrasesMax: 5,
	waitPhraseCharsMax: 40,
} as const;

/** Harus sama dengan default kolom `voiceWaitPhrases` di schema.prisma. */
export const DEFAULT_WAIT_PHRASES = [
	"Tunggu sebentar ya",
	"Saya carikan dulu ya",
] as const;

/** Harus sama dengan default kolom `assistant_style` di schema.prisma. */
export const DEFAULT_EXPRESSIVE_WORDS = [
	"Hmm",
	"Oke deh",
	"Boleh kok",
	"Wahh",
	"Siap",
] as const;

export const DEFAULT_ASSISTANT_STYLE: Readonly<AssistantStyleDto> = {
	chatStyle: "ramah",
	voiceStyle: "santai",
	expressiveWords: [...DEFAULT_EXPRESSIVE_WORDS],
	expressiveFrequency: "jarang",
	extraRules: [],
	waitSoundEnabled: false,
	waitSoundVolume: "sedang",
	autoPointEnabled: true,
	voiceFillerEnabled: false,
	voiceEchoFilterEnabled: false,
	voiceOpenerEnabled: false,
	voiceStyleLayerEnabled: false,
	voiceWaitPhrases: [...DEFAULT_WAIT_PHRASES],
};

export function isStylePreset(value: unknown): value is StylePreset {
	return STYLE_PRESETS.includes(value as StylePreset);
}

export function isExpressiveFrequency(
	value: unknown,
): value is ExpressiveFrequency {
	return EXPRESSIVE_FREQUENCIES.includes(value as ExpressiveFrequency);
}

export function isWaitSoundVolume(value: unknown): value is WaitSoundVolume {
	return WAIT_SOUND_VOLUMES.includes(value as WaitSoundVolume);
}
